import { mapGetters } from 'vuex'
import moment from 'moment'

// Shared helpers for the Backup restore module (BACKUP_RESTORE).

// Last day the old server took writes (it crashed on the morning of 23 Sep
// 2026). The day-chips of the filter bar go from `info.since` to this day.
export const BACKUP_LAST_DAY = '2026-09-23'

export const MATCH_STATUSES = ['MISSING', 'ID_CONFLICT', 'CHANGED', 'EXISTS', 'RESTORED']
export const RESTORABLE_STATUSES = ['MISSING', 'ID_CONFLICT']

export const STATUS_META = {
  MISSING: { variant: 'primary', icon: 'fa-solid fa-circle-plus' },
  ID_CONFLICT: { variant: 'warning', icon: 'fa-solid fa-triangle-exclamation' },
  CHANGED: { variant: 'info', icon: 'fa-solid fa-code-compare' },
  EXISTS: { variant: 'secondary', icon: 'fa-solid fa-check' },
  RESTORED: { variant: 'success', icon: 'fa-solid fa-rotate-left' },
}

export const KIND_ICONS = {
  CLIENT: 'fa-solid fa-user',
  ACCOUNT: 'fa-solid fa-id-card',
  FIELD: 'fa-solid fa-code-compare',
  MONETARY: 'fa-solid fa-coins',
  TRADE: 'fa-solid fa-chart-line',
  NOTE: 'fa-regular fa-note-sticky',
}

export const isRestorable = status => RESTORABLE_STATUSES.includes(status)

// Empty POST /backup/restore body.
export const emptyRestoreRequest = clientBackupId => ({
  clientBackupId: clientBackupId || null,
  restoreClient: false,
  accounts: [],
  fields: [],
  monetary: [],
  trades: [],
  notes: [],
})

// Bulk (tab) restores: one request per backup client, so the server always
// knows which client the items belong to.
export const groupRestoreRequests = ({ monetary = [], trades = [], notes = [] }) => {
  const byClient = {}
  const req = id => {
    const key = id == null ? '' : String(id)
    if (!byClient[key]) byClient[key] = emptyRestoreRequest(id)
    return byClient[key]
  }
  monetary.forEach(m => req(m.row.clientBackupId).monetary.push({ backupId: m.row.backupId, applyToBalance: m.applyToBalance === true }))
  trades.forEach(t => req(t.clientBackupId).trades.push(t.backupId))
  notes.forEach(n => req(n.clientBackupId).notes.push(n.backupId))
  return Object.values(byClient)
}

// [{ currency, total }] of the transactions that will be booked on balances.
export const balanceTotals = (monetary = []) => {
  const map = {}
  monetary.filter(m => m.applyToBalance).forEach(m => {
    const c = m.row.currency || 'EUR'
    map[c] = (map[c] || 0) + (Number(m.row.amount) || 0)
  })
  return Object.keys(map).map(currency => ({ currency, total: map[currency] }))
}

// Tracked client fields whose values are ids → resolved to names for display.
const USER_FIELDS = ['representativeUserId', 'retentionUserId', 'userId', 'creatorUserId', 'depositorUserId']

export default {
  computed: {
    ...mapGetters('data', ['getUserFullName', 'getOrganizationNameById', 'getBusinessUnitNameById', 'getAffiliateNameById']),
    ...mapGetters('clients', ['allStatuses']),
    bkStatusMap() {
      return (this.allStatuses || []).reduce((map, s) => {
        map[s.id] = s
        return map
      }, {})
    },
  },
  methods: {
    bkStatusLabel(status) {
      return this.bkT(`backup.status.${status}`, status)
    },
    bkReasonLabel(reason) {
      if (!reason) return ''
      return this.bkT(`backup.reason.${reason}`, reason)
    },
    // Translation with a raw fallback (locale files may not know every enum value).
    bkT(key, fallback) {
      if (this.$te(key)) return this.$t(key)
      if (this.$te(key, 'en')) return this.$t(key, 'en')
      return fallback == null ? '' : String(fallback)
    },
    bkActionLabel(action) {
      if (!action) return ''
      return this.bkT(`monetaryActions.${action}.name`, action)
    },
    bkTypeLabel(type) {
      return this.bkT(`backup.monetaryType.${type}`, type)
    },
    bkMoney(amount, currency) {
      const n = Number(amount) || 0
      try {
        return this.convertToEURO(n, currency || 'EUR')
      } catch (e) {
        return `${n.toFixed(2)} ${currency || ''}`.trim()
      }
    },
    // Colour by money direction: in = green, out = red, bonus = purple, TXF = grey.
    bkAmountClass(row) {
      if (!row) return ''
      if (row.action === 'TAXES') return 'bk-amt--txf'
      if (row.action === 'CREDIT' || row.action === 'BONUS') return 'bk-amt--bonus'
      if (row.type === 'WITHDRAWAL' || Number(row.amount) < 0) return 'bk-amt--out'
      return 'bk-amt--in'
    },
    bkDateTime(value) {
      if (!value) return ''
      return this.$options.filters.momentDateTime(value)
    },
    bkDay(value) {
      if (!value) return ''
      return moment.utc(value, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD', moment.ISO_8601]).local().format('D MMM YYYY')
    },
    // Same UTC → local conversion as the momentDateTime filter.
    bkTime(value) {
      if (!value) return ''
      return moment.utc(value, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', moment.ISO_8601]).local().format('HH:mm')
    },
    bkClientStatusName(id) {
      if (id == null || id === '') return ''
      const s = this.bkStatusMap[id]
      return s ? s.status : String(id)
    },
    bkClientStatusColor(id) {
      const s = this.bkStatusMap[id]
      return s ? s.color : null
    },
    // Field value → human label (ids resolved through the data store).
    bkFieldValue(field, value) {
      if (value == null || value === '') return ''
      if (field === 'statusId') return this.bkClientStatusName(value)
      if (USER_FIELDS.includes(field)) {
        const name = this.getUserFullName(value)
        return name && name !== 'Unknown' ? name : `#${value}`
      }
      if (field === 'businessUnitId') return this.getBusinessUnitNameById(value) || `#${value}`
      if (field === 'orgId') return this.getOrganizationNameById(value) || `#${value}`
      if (field === 'affiliateId') return this.getAffiliateNameById(value) || `#${value}`
      return String(value)
    },
    bkFieldLabel(field) {
      return this.bkT(`backup.fields.${field}`, field)
    },
    bkUserName(id) {
      if (id == null || id === '') return ''
      const name = this.getUserFullName(id)
      return name && name !== 'Unknown' ? name : `#${id}`
    },
    bkClientName(row) {
      if (!row) return ''
      const n = `${row.firstName || ''} ${row.lastName || ''}`.trim()
      return n || row.clientName || `#${row.backupId || row.clientBackupId || ''}`
    },
    bkInitials(row) {
      const f = ((row && row.firstName) || ' ')[0] || ''
      const l = ((row && row.lastName) || ' ')[0] || ''
      return (f + l).toUpperCase().trim() || '?'
    },
  },
}
